// src/components/admin/page-management/section-forms/GoogleMapsForm.js
import React, { useState } from 'react';

const GoogleMapsForm = ({ content, updateContent }) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showMarkers, setShowMarkers] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  
  // Add a new location marker
  const addMarker = () => {
    const newMarkers = [...(content?.markers || [])];
    newMarkers.push({
      id: `marker_${Date.now()}`,
      address: process.env.REACT_APP_COMPANY_ADDRESS || '102-1127 14th St W, North Vancouver, BC, V7P 1J9',
      title: 'Our Location',
      description: 'Visit us today!',
      lat: '',
      lng: ''
    });
    updateContent('markers', newMarkers);
  };
  
  // Update a marker field
  const updateMarker = (index, field, value) => {
    const markers = [...(content?.markers || [])];
    markers[index] = {
      ...markers[index],
      [field]: value
    };
    updateContent('markers', markers);
  };
  
  // Remove a marker
  const removeMarker = (index) => {
    const markers = [...(content?.markers || [])];
    markers.splice(index, 1);
    updateContent('markers', markers);
  };

  const InfoTooltip = ({ children, tooltip }) => (
    <div className="relative group inline-block">
      {children}
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
        {tooltip}
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
      </div>
    </div>
  );

  const FormSection = ({ title, children, className = "" }) => (
    <div className={`bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200 ${className}`}>
      <div className="p-6">
        {title && (
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <div className="w-2 h-6 bg-blue-500 rounded-full mr-3"></div>
            {title}
          </h3>
        )}
        {children}
      </div>
    </div>
  );

  const InputField = ({ label, type = "text", value, onChange, placeholder, tooltip, children, className = "" }) => (
    <div className={`space-y-2 ${className}`}>
      <label className="flex items-center text-sm font-medium text-gray-700">
        {label}
        {tooltip && (
          <InfoTooltip tooltip={tooltip}>
            <svg className="w-4 h-4 ml-1 text-gray-400 hover:text-gray-600 cursor-help" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
            </svg>
          </InfoTooltip>
        )}
      </label>
      {children || (
        <input
          type={type}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 bg-white hover:border-gray-400"
          value={value}
          onChange={onChange}
          placeholder={placeholder}
        />
      )}
    </div>
  );

  const SelectField = ({ label, value, onChange, options, tooltip, className = "" }) => (
    <InputField label={label} tooltip={tooltip} className={className}>
      <select
        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 bg-white hover:border-gray-400"
        value={value}
        onChange={onChange}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </InputField>
  );

  const CheckboxField = ({ label, checked, onChange, tooltip, className = "" }) => (
    <div className={`flex items-start space-x-3 ${className}`}>
      <div className="flex items-center h-6">
        <input
          type="checkbox"
          className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 transition-colors duration-200"
          checked={checked}
          onChange={onChange}
        />
      </div>
      <div className="flex-1">
        <label className="flex items-center text-sm font-medium text-gray-700 cursor-pointer">
          {label}
          {tooltip && (
            <InfoTooltip tooltip={tooltip}>
              <svg className="w-4 h-4 ml-1 text-gray-400 hover:text-gray-600 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
              </svg>
            </InfoTooltip>
          )}
        </label>
      </div>
    </div>
  );

  const TabButton = ({ active, onClick, children, icon }) => (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center px-6 py-3 rounded-lg font-medium text-sm transition-all duration-200 ${
        active 
          ? 'bg-blue-600 text-white shadow-md' 
          : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800'
      }`}
    >
      {icon && <span className="mr-2">{icon}</span>}
      {children}
    </button>
  );

  return (
    <div className="max-w-4xl mx-auto space-y-8 p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="text-center space-y-2">
        <h2 className="text-3xl font-bold text-gray-900">Google Maps Setup</h2>
        <p className="text-gray-600">Configure your interactive map with just a few clicks</p>
      </div>

      {/* Navigation Tabs */}
      <div className="flex flex-wrap gap-3 justify-center">
        <TabButton 
          active={activeTab === 'basic'} 
          onClick={() => setActiveTab('basic')}
        >
          Basic Settings
        </TabButton>
        <TabButton 
          active={activeTab === 'markers'} 
          onClick={() => setActiveTab('markers')}
        >
          Location Markers
        </TabButton>
        <TabButton 
          active={activeTab === 'advanced'} 
          onClick={() => setActiveTab('advanced')}
        >
          Advanced Options
        </TabButton>
      </div>

      {/* Basic Settings Tab */}
      {activeTab === 'basic' && (
        <FormSection title="Basic Map Configuration">
          <div className="grid md:grid-cols-2 gap-6">
            <InputField
              label="Section Title"
              value={content?.title || ''}
              onChange={(e) => updateContent('title', e.target.value)}
              placeholder="Our Location"
              tooltip="Optional title that appears above your map"
            />

            <InputField
              label="Map Address"
              value={content?.address || process.env.REACT_APP_COMPANY_ADDRESS || '102-1127 14th St W, North Vancouver, BC, V7P 1J9'}
              onChange={(e) => updateContent('address', e.target.value)}
              placeholder="Enter your business address"
              tooltip="This will be the center point of your map"
            />

            <SelectField
              label="Map Height"
              value={content?.height || '400px'}
              onChange={(e) => updateContent('height', e.target.value)}
              tooltip="How tall your map will appear on the page"
              options={[
                { value: '300px', label: '300px - Small' },
                { value: '400px', label: '400px - Medium' },
                { value: '500px', label: '500px - Large' },
                { value: '600px', label: '600px - Extra Large' },
                { value: '50vh', label: '50vh - Half Screen' },
                { value: '100vh', label: '100vh - Full Screen' }
              ]}
            />

            <SelectField
              label="Zoom Level"
              value={content?.zoom || 15}
              onChange={(e) => updateContent('zoom', parseInt(e.target.value))}
              tooltip="Higher numbers show more detail. 15-17 works great for businesses"
              options={Array.from({ length: 19 }, (_, i) => ({
                value: i + 1,
                label: `${i + 1} - ${i < 5 ? 'World View' : i < 10 ? 'City View' : i < 15 ? 'Neighborhood' : 'Street Level'}`
              }))}
            />

            <SelectField
              label="Map Type"
              value={content?.mapType || 'roadmap'}
              onChange={(e) => updateContent('mapType', e.target.value)}
              tooltip="Choose how your map looks"
              options={[
                { value: 'roadmap', label: 'Road Map (Default)' },
                { value: 'satellite', label: 'Satellite View' },
                { value: 'hybrid', label: 'Hybrid (Satellite + Roads)' },
                { value: 'terrain', label: 'Terrain View' }
              ]}
            />

            <SelectField
              label="Map Load Method"
              value={content?.loadMethod || 'embed'}
              onChange={(e) => updateContent('loadMethod', e.target.value)}
              tooltip="Simple embed is easier, JavaScript API offers more features"
              options={[
                { value: 'embed', label: 'Simple Embed (Recommended)' },
                { value: 'javascript', label: 'JavaScript API (Advanced)' }
              ]}
            />
          </div>

          <div className="mt-8 grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-800 flex items-center">
                Map Controls
              </h4>
              <CheckboxField
                label="Show Map Controls"
                checked={content?.showControls !== false}
                onChange={(e) => updateContent('showControls', e.target.checked)}
                tooltip="Enables zoom, pan, and navigation controls"
              />
              <CheckboxField
                label="Allow Fullscreen"
                checked={content?.allowFullscreen !== false}
                onChange={(e) => updateContent('allowFullscreen', e.target.checked)}
                tooltip="Users can expand the map to fullscreen"
              />
              <CheckboxField
                label="Allow Street View"
                checked={content?.allowStreetView !== false}
                onChange={(e) => updateContent('allowStreetView', e.target.checked)}
                tooltip="Users can access Google Street View"
              />
            </div>

            <InputField
              label="Backup Image"
              value={content?.placeholderImage || '/images/map-placeholder.jpg'}
              onChange={(e) => updateContent('placeholderImage', e.target.value)}
              placeholder="/images/map-placeholder.jpg"
              tooltip="Image shown while the map loads or if it fails"
            />
          </div>
        </FormSection>
      )}

      {/* Markers Tab */}
      {activeTab === 'markers' && (
        <FormSection title="Location Markers">
          <div className="text-center mb-8">
            <p className="text-gray-600 mb-4">Add custom markers to highlight specific locations on your map</p>
            <button
              type="button"
              onClick={addMarker}
              className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Add New Marker
            </button>
          </div>

          {(content?.markers || []).length === 0 ? (
            <div className="text-center py-12 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">No markers yet</h3>
              <p className="text-gray-600">Click "Add New Marker" to get started</p>
            </div>
          ) : (
            <div className="space-y-6">
              {(content?.markers || []).map((marker, index) => (
                <div key={marker.id} className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-6 hover:shadow-md transition-shadow duration-200">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="text-lg font-semibold text-gray-800 flex items-center">
                      <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">
                        {index + 1}
                      </span>
                      {marker.title || `Location ${index + 1}`}
                    </h4>
                    <button 
                      type="button"
                      onClick={() => removeMarker(index)}
                      className="flex items-center px-3 py-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors duration-200"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Remove
                    </button>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <InputField
                      label="Address"
                      value={marker.address}
                      onChange={(e) => updateMarker(index, 'address', e.target.value)}
                      placeholder="Enter the full address"
                    />
                    
                    <InputField
                      label="Marker Title"
                      value={marker.title}
                      onChange={(e) => updateMarker(index, 'title', e.target.value)}
                      placeholder="e.g., Main Office, Store Location"
                    />
                    
                    <div className="md:col-span-2">
                      <InputField label="Description">
                        <textarea
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 bg-white hover:border-gray-400"
                          value={marker.description}
                          onChange={(e) => updateMarker(index, 'description', e.target.value)}
                          placeholder="Brief description about this location"
                          rows="3"
                        />
                      </InputField>
                    </div>
                    
                    <InputField
                      label="Latitude (Optional)"
                      value={marker.lat}
                      onChange={(e) => updateMarker(index, 'lat', e.target.value)}
                      placeholder="e.g., 49.3229"
                      tooltip="For precise positioning. Leave blank to use address"
                    />
                    
                    <InputField
                      label="Longitude (Optional)"
                      value={marker.lng}
                      onChange={(e) => updateMarker(index, 'lng', e.target.value)}
                      placeholder="e.g., -123.1235"
                      tooltip="For precise positioning. Leave blank to use address"
                    />
                    
                    <div className="md:col-span-2">
                      <InputField
                        label="Custom Marker Icon (Optional)"
                        value={marker.iconUrl || ''}
                        onChange={(e) => updateMarker(index, 'iconUrl', e.target.value)}
                        placeholder="/images/custom-marker.png"
                        tooltip="URL to a custom icon image. Leave blank for default Google marker"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </FormSection>
      )}

      {/* Advanced Tab */}
      {activeTab === 'advanced' && (
        <FormSection title="Advanced Styling Options">
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-amber-500 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="text-amber-800 font-medium">Advanced Features</p>
                <p className="text-amber-700 text-sm">These options require the JavaScript API method for full functionality</p>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <SelectField
              label="Map Style Theme"
              value={content?.mapStyle || 'default'}
              onChange={(e) => updateContent('mapStyle', e.target.value)}
              tooltip="Choose a visual theme for your map"
              options={[
                { value: 'default', label: 'Default' },
                { value: 'silver', label: ' Silver' },
                { value: 'retro', label: ' Retro' },
                { value: 'dark', label: ' Dark' },
                { value: 'night', label: ' Night' },
                { value: 'aubergine', label: 'Aubergine' },
                { value: 'custom', label: 'Custom Style' }
              ]}
            />

            <SelectField
              label="Corner Rounding"
              value={content?.borderRadius || 'rounded-xl'}
              onChange={(e) => updateContent('borderRadius', e.target.value)}
              tooltip="How rounded the map corners should be"
              options={[
                { value: 'rounded-none', label: 'Sharp Corners' },
                { value: 'rounded', label: ' Slightly Rounded' },
                { value: 'rounded-md', label: ' Medium Rounded' },
                { value: 'rounded-lg', label: ' Well Rounded' },
                { value: 'rounded-xl', label: ' Very Rounded' }
              ]}
            />

            <SelectField
              label="Shadow Effect"
              value={content?.shadowStyle || 'shadow-lg'}
              onChange={(e) => updateContent('shadowStyle', e.target.value)}
              tooltip="Drop shadow effect around the map"
              options={[
                { value: 'shadow-none', label: 'No Shadow' },
                { value: 'shadow-sm', label: ' Subtle' },
                { value: 'shadow', label: 'Light' },
                { value: 'shadow-md', label: 'Medium' },
                { value: 'shadow-lg', label: 'Strong' },
                { value: 'shadow-xl', label: 'Very Strong' }
              ]}
            />

            <div className="space-y-4">
              <h4 className="font-semibold text-gray-800 flex items-center">
                Additional Features
              </h4>
              <CheckboxField
                label="Show Traffic Layer"
                checked={content?.showTraffic === true}
                onChange={(e) => updateContent('showTraffic', e.target.checked)}
                tooltip="Display real-time traffic information (JavaScript API only)"
              />
              <CheckboxField
                label="Auto-open Info Windows"
                checked={content?.showInfoWindowsOnLoad === true}
                onChange={(e) => updateContent('showInfoWindowsOnLoad', e.target.checked)}
                tooltip="Automatically show marker info when the map loads"
              />
            </div>
          </div>

          {content?.mapStyle === 'custom' && (
            <div className="mt-6">
              <InputField label="Custom Style JSON">
                <textarea
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 bg-white hover:border-gray-400 font-mono text-sm"
                  value={content?.customMapStyle || '[]'}
                  onChange={(e) => updateContent('customMapStyle', e.target.value)}
                  placeholder='[{"featureType":"all","elementType":"labels.text.fill","stylers":[{"saturation":36},{"color":"#333333"},{"lightness":40}]}]'
                  rows="6"
                />
                <p className="text-sm text-gray-600 mt-2">
                  Create custom styles at{' '}
                  <a 
                    href="https://mapstyle.withgoogle.com/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    Google's Map Style Wizard
                  </a>
                </p>
              </InputField>
            </div>
          )}

          <div className="mt-6">
            <InputField label="Custom CSS (Advanced Users Only)">
              <textarea
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 bg-white hover:border-gray-400 font-mono text-sm"
                value={content?.customCss || ''}
                onChange={(e) => updateContent('customCss', e.target.value)}
                placeholder=".map-container { /* your custom styles here */ }"
                rows="4"
              />
            </InputField>
          </div>
        </FormSection>
      )}

      {/* Help Section */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl border border-green-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
          <svg className="w-6 h-6 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Quick Tips
        </h3>
        <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-700">
          <div className="flex items-start">
            <span>Use zoom level 15-17 for the best business location view</span>
          </div>
          <div className="flex items-start">
            <span>Simple Embed works great for most use cases</span>
          </div>
          <div className="flex items-start">
            <span>Add multiple markers to show different locations</span>
          </div>
          <div className="flex items-start">
            <span>Custom styles require JavaScript API method</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoogleMapsForm;



// // src/components/admin/page-management/section-forms/GoogleMapsForm.js
// import React, { useState } from 'react';

// const GoogleMapsForm = ({ content, updateContent }) => {
//   const [showAdvanced, setShowAdvanced] = useState(false);
//   const [showMarkers, setShowMarkers] = useState(false);
  
//   // Add a new location marker
//   const addMarker = () => {
//     const newMarkers = [...(content?.markers || [])];
//     newMarkers.push({
//       id: `marker_${Date.now()}`,
//       address: process.env.REACT_APP_COMPANY_ADDRESS || '102-1127 14th St W, North Vancouver, BC, V7P 1J9',
//       title: 'Our Location',
//       description: 'Visit us today!',
//       lat: '',
//       lng: ''
//     });
//     updateContent('markers', newMarkers);
//   };
  
//   // Update a marker field
//   const updateMarker = (index, field, value) => {
//     const markers = [...(content?.markers || [])];
//     markers[index] = {
//       ...markers[index],
//       [field]: value
//     };
//     updateContent('markers', markers);
//   };
  
//   // Remove a marker
//   const removeMarker = (index) => {
//     const markers = [...(content?.markers || [])];
//     markers.splice(index, 1);
//     updateContent('markers', markers);
//   };

//   return (
//     <div className="space-y-4">
//       <h3 className="text-lg font-medium">Google Maps Configuration</h3>
      
//       <div className="mb-4">
//         <label className="block text-sm font-medium text-gray-700 mb-1">
//           Section Title (Optional)
//         </label>
//         <input
//           type="text"
//           className="w-full p-2 border rounded"
//           value={content?.title || ''}
//           onChange={(e) => updateContent('title', e.target.value)}
//           placeholder="Our Location"
//         />
//         <p className="text-xs text-gray-500 mt-1">
//           Leave blank if you don't want a title above the map.
//         </p>
//       </div>
      
//       <div className="mb-4">
//         <label className="block text-sm font-medium text-gray-700 mb-1">
//           Default Map Address
//         </label>
//         <input
//           type="text"
//           className="w-full p-2 border rounded"
//           value={content?.address || process.env.REACT_APP_COMPANY_ADDRESS || '102-1127 14th St W, North Vancouver, BC, V7P 1J9'}
//           onChange={(e) => updateContent('address', e.target.value)}
//           placeholder="102-1127 14th St W, North Vancouver, BC, V7P 1J9"
//         />
//         <p className="text-xs text-gray-500 mt-1">
//           This will be the default center of the map.
//         </p>
//       </div>
      
//       <div className="mb-4">
//         <label className="block text-sm font-medium text-gray-700 mb-1">
//           Map Height
//         </label>
//         <input
//           type="text"
//           className="w-full p-2 border rounded"
//           value={content?.height || '400px'}
//           onChange={(e) => updateContent('height', e.target.value)}
//           placeholder="400px"
//         />
//         <p className="text-xs text-gray-500 mt-1">
//           Set the height of the map (e.g., 400px, 500px, 50vh).
//         </p>
//       </div>
      
//       <div className="mb-4">
//         <label className="block text-sm font-medium text-gray-700 mb-1">
//           Map Zoom Level
//         </label>
//         <select
//           className="w-full p-2 border rounded"
//           value={content?.zoom || 15}
//           onChange={(e) => updateContent('zoom', parseInt(e.target.value))}
//         >
//           {[...Array(19)].map((_, i) => (
//             <option key={i + 1} value={i + 1}>
//               {i + 1} - {i < 10 ? 'Far' : i < 15 ? 'Medium' : 'Close'}
//             </option>
//           ))}
//         </select>
//         <p className="text-xs text-gray-500 mt-1">
//           Higher numbers zoom in closer. 15-17 is good for showing a single building.
//         </p>
//       </div>
      
//       <div className="mb-4">
//         <label className="block text-sm font-medium text-gray-700 mb-1">
//           Map Type
//         </label>
//         <select
//           className="w-full p-2 border rounded"
//           value={content?.mapType || 'roadmap'}
//           onChange={(e) => updateContent('mapType', e.target.value)}
//         >
//           <option value="roadmap">Road Map (Default)</option>
//           <option value="satellite">Satellite</option>
//           <option value="hybrid">Hybrid (Satellite + Roads)</option>
//           <option value="terrain">Terrain</option>
//         </select>
//       </div>
      
//       <div className="mb-4">
//         <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
//           <input
//             type="checkbox"
//             className="mr-2 h-4 w-4"
//             checked={content?.showControls !== false}
//             onChange={(e) => updateContent('showControls', e.target.checked)}
//           />
//           Show Map Controls
//         </label>
//         <p className="text-xs text-gray-500 mt-1 ml-6">
//           Enables zoom, pan, and street view controls on the map.
//         </p>
//       </div>
      
//       <div className="mb-4">
//         <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
//           <input
//             type="checkbox"
//             className="mr-2 h-4 w-4"
//             checked={content?.allowFullscreen !== false}
//             onChange={(e) => updateContent('allowFullscreen', e.target.checked)}
//           />
//           Allow Fullscreen Option
//         </label>
//       </div>
      
//       <div className="mb-4">
//         <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
//           <input
//             type="checkbox"
//             className="mr-2 h-4 w-4"
//             checked={content?.allowStreetView !== false}
//             onChange={(e) => updateContent('allowStreetView', e.target.checked)}
//           />
//           Allow Street View
//         </label>
//       </div>
      
//       <div className="mb-4">
//         <label className="block text-sm font-medium text-gray-700 mb-1">
//           Map Load Method
//         </label>
//         <select
//           className="w-full p-2 border rounded"
//           value={content?.loadMethod || 'embed'}
//           onChange={(e) => updateContent('loadMethod', e.target.value)}
//         >
//           <option value="embed">Simple Embed (Less Customizable)</option>
//           <option value="javascript">JavaScript API (More Customizable)</option>
//         </select>
//         <p className="text-xs text-gray-500 mt-1">
//           The JavaScript API option offers more customization but requires an API key configuration.
//         </p>
//       </div>
      
//       <div className="mb-4">
//         <label className="block text-sm font-medium text-gray-700 mb-1">
//           Placeholder Image URL
//         </label>
//         <input
//           type="text"
//           className="w-full p-2 border rounded"
//           value={content?.placeholderImage || '/images/map-placeholder.jpg'}
//           onChange={(e) => updateContent('placeholderImage', e.target.value)}
//           placeholder="/images/map-placeholder.jpg"
//         />
//         <p className="text-xs text-gray-500 mt-1">
//           Image to display while the map is loading or if it fails.
//         </p>
//       </div>
      
//       {/* Multiple Markers Configuration */}
//       <div className="border-t border-gray-200 pt-4">
//         <button
//           type="button"
//           className="text-sm text-blue-600 hover:text-blue-800 mb-4"
//           onClick={() => setShowMarkers(!showMarkers)}
//         >
//           {showMarkers ? 'Hide Location Markers' : 'Configure Multiple Location Markers'}
//         </button>
        
//         {showMarkers && (
//           <div className="space-y-4">
//             <div className="flex items-center justify-between mb-3">
//               <h4 className="font-medium text-gray-800">Location Markers</h4>
//               <button
//                 type="button"
//                 onClick={addMarker}
//                 className="text-sm bg-blue-50 hover:bg-blue-100 text-blue-600 py-1 px-3 rounded border border-blue-200"
//               >
//                 + Add Marker
//               </button>
//             </div>
            
//             {(content?.markers || []).length === 0 ? (
//               <p className="text-sm text-gray-500 italic p-4 bg-gray-50 rounded">
//                 No custom markers added. Add a marker to show specific locations on the map.
//               </p>
//             ) : (
//               (content?.markers || []).map((marker, index) => (
//                 <div key={marker.id} className="border rounded-lg p-4 mb-4 bg-gray-50">
//                   <div className="flex justify-between items-center mb-3">
//                     <h5 className="font-medium">{marker.title || `Location ${index + 1}`}</h5>
//                     <button 
//                       type="button"
//                       onClick={() => removeMarker(index)}
//                       className="text-red-500 hover:text-red-700"
//                     >
//                       Remove
//                     </button>
//                   </div>
                  
//                   <div className="space-y-3">
//                     <div>
//                       <label className="block text-xs font-medium text-gray-700 mb-1">
//                         Address
//                       </label>
//                       <input
//                         type="text"
//                         className="w-full p-2 border rounded text-sm"
//                         value={marker.address}
//                         onChange={(e) => updateMarker(index, 'address', e.target.value)}
//                         placeholder="Enter address"
//                       />
//                     </div>
                    
//                     <div>
//                       <label className="block text-xs font-medium text-gray-700 mb-1">
//                         Marker Title
//                       </label>
//                       <input
//                         type="text"
//                         className="w-full p-2 border rounded text-sm"
//                         value={marker.title}
//                         onChange={(e) => updateMarker(index, 'title', e.target.value)}
//                         placeholder="Location title"
//                       />
//                     </div>
                    
//                     <div>
//                       <label className="block text-xs font-medium text-gray-700 mb-1">
//                         Description
//                       </label>
//                       <textarea
//                         className="w-full p-2 border rounded text-sm"
//                         value={marker.description}
//                         onChange={(e) => updateMarker(index, 'description', e.target.value)}
//                         placeholder="Location description"
//                         rows="2"
//                       />
//                     </div>
                    
//                     <div className="grid grid-cols-2 gap-3">
//                       <div>
//                         <label className="block text-xs font-medium text-gray-700 mb-1">
//                           Latitude (Optional)
//                         </label>
//                         <input
//                           type="text"
//                           className="w-full p-2 border rounded text-sm"
//                           value={marker.lat}
//                           onChange={(e) => updateMarker(index, 'lat', e.target.value)}
//                           placeholder="e.g. 49.3229"
//                         />
//                       </div>
                      
//                       <div>
//                         <label className="block text-xs font-medium text-gray-700 mb-1">
//                           Longitude (Optional)
//                         </label>
//                         <input
//                           type="text"
//                           className="w-full p-2 border rounded text-sm"
//                           value={marker.lng}
//                           onChange={(e) => updateMarker(index, 'lng', e.target.value)}
//                           placeholder="e.g. -123.1235"
//                         />
//                       </div>
//                     </div>
                    
//                     <div>
//                       <label className="block text-xs font-medium text-gray-700 mb-1">
//                         Marker Icon URL (Optional)
//                       </label>
//                       <input
//                         type="text"
//                         className="w-full p-2 border rounded text-sm"
//                         value={marker.iconUrl || ''}
//                         onChange={(e) => updateMarker(index, 'iconUrl', e.target.value)}
//                         placeholder="/images/custom-marker.png"
//                       />
//                       <p className="text-xs text-gray-500 mt-1">
//                         Leave blank to use the default Google Maps marker.
//                       </p>
//                     </div>
//                   </div>
//                 </div>
//               ))
//             )}
            
//             <p className="text-xs text-gray-500 mt-2">
//               Note: For precise marker placement, you can enter latitude and longitude coordinates. 
//               Otherwise, Google Maps will geocode the address automatically.
//             </p>
//           </div>
//         )}
//       </div>
      
//       {/* Advanced Options */}
//       <div className="border-t border-gray-200 pt-4">
//         <button
//           type="button"
//           className="text-sm text-blue-600 hover:text-blue-800 mb-4"
//           onClick={() => setShowAdvanced(!showAdvanced)}
//         >
//           {showAdvanced ? 'Hide Advanced Options' : 'Show Advanced Options'}
//         </button>
        
//         {showAdvanced && (
//           <div className="space-y-4">
//             <h4 className="font-medium text-gray-800 mb-3">Advanced Map Options</h4>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium text-gray-700 mb-1">
//                 Map Style
//               </label>
//               <select
//                 className="w-full p-2 border rounded"
//                 value={content?.mapStyle || 'default'}
//                 onChange={(e) => updateContent('mapStyle', e.target.value)}
//               >
//                 <option value="default">Default</option>
//                 <option value="silver">Silver</option>
//                 <option value="retro">Retro</option>
//                 <option value="dark">Dark</option>
//                 <option value="night">Night</option>
//                 <option value="aubergine">Aubergine</option>
//                 <option value="custom">Custom JSON Style</option>
//               </select>
//               <p className="text-xs text-gray-500 mt-1">
//                 Choose a predefined style for the map appearance (JavaScript API only).
//               </p>
//             </div>
            
//             {content?.mapStyle === 'custom' && (
//               <div className="mb-4">
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   Custom Map Style (JSON)
//                 </label>
//                 <textarea
//                   className="w-full p-2 border rounded font-mono text-sm"
//                   value={content?.customMapStyle || '[]'}
//                   onChange={(e) => updateContent('customMapStyle', e.target.value)}
//                   placeholder='[{"featureType":"all","elementType":"labels.text.fill","stylers":[{"saturation":36},{"color":"#333333"},{"lightness":40}]}]'
//                   rows="6"
//                 />
//                 <p className="text-xs text-gray-500 mt-1">
//                   Paste custom map style JSON from <a href="https://mapstyle.withgoogle.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google's Map Style Wizard</a>.
//                 </p>
//               </div>
//             )}
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium text-gray-700 mb-1">
//                 Border Radius
//               </label>
//               <select
//                 className="w-full p-2 border rounded"
//                 value={content?.borderRadius || 'rounded-xl'}
//                 onChange={(e) => updateContent('borderRadius', e.target.value)}
//               >
//                 <option value="rounded-none">None</option>
//                 <option value="rounded">Small</option>
//                 <option value="rounded-md">Medium</option>
//                 <option value="rounded-lg">Large</option>
//                 <option value="rounded-xl">Extra Large</option>
//               </select>
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium text-gray-700 mb-1">
//                 Shadow Style
//               </label>
//               <select
//                 className="w-full p-2 border rounded"
//                 value={content?.shadowStyle || 'shadow-lg'}
//                 onChange={(e) => updateContent('shadowStyle', e.target.value)}
//               >
//                 <option value="shadow-none">None</option>
//                 <option value="shadow-sm">Small</option>
//                 <option value="shadow">Medium</option>
//                 <option value="shadow-md">Large</option>
//                 <option value="shadow-lg">Extra Large</option>
//                 <option value="shadow-xl">2XL</option>
//               </select>
//             </div>
            
//             <div className="mb-4">
//               <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
//                 <input
//                   type="checkbox"
//                   className="mr-2 h-4 w-4"
//                   checked={content?.showTraffic === true}
//                   onChange={(e) => updateContent('showTraffic', e.target.checked)}
//                 />
//                 Show Traffic Layer
//               </label>
//               <p className="text-xs text-gray-500 mt-1 ml-6">
//                 Display real-time traffic information (JavaScript API only).
//               </p>
//             </div>
            
//             <div className="mb-4">
//               <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
//                 <input
//                   type="checkbox"
//                   className="mr-2 h-4 w-4"
//                   checked={content?.showInfoWindowsOnLoad === true}
//                   onChange={(e) => updateContent('showInfoWindowsOnLoad', e.target.checked)}
//                 />
//                 Show Info Windows on Load
//               </label>
//               <p className="text-xs text-gray-500 mt-1 ml-6">
//                 Automatically open info windows when the map loads (JavaScript API only).
//               </p>
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium text-gray-700 mb-1">
//                 Custom CSS (Advanced)
//               </label>
//               <textarea
//                 className="w-full p-2 border rounded font-mono text-sm"
//                 value={content?.customCss || ''}
//                 onChange={(e) => updateContent('customCss', e.target.value)}
//                 placeholder=".map-container { /* custom styles */ }"
//                 rows="4"
//               />
//               <p className="text-xs text-gray-500 mt-1">
//                 Add custom CSS to further customize your map container (for advanced users).
//               </p>
//             </div>
//           </div>
//         )}
//       </div>
      
//       <div className="bg-blue-50 p-4 rounded-md mb-6">
//         <h4 className="text-blue-800 font-medium mb-2">Important Notes</h4>
//         <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
//           <li>Advanced features like custom styles require using the JavaScript API option</li>
//           <li>For multiple markers, the JavaScript API is recommended</li>
//           <li>The placeholder image will show until the map is fully loaded</li>
//         </ul>
//       </div>
//     </div>
//   );
// };

// export default GoogleMapsForm;







// // src/components/admin/page-management/section-forms/GoogleMapsForm.js
// import React from 'react';

// const GoogleMapsForm = ({ content, updateContent }) => {
//   return (
//     <div className="space-y-4">
//       <h3 className="text-lg font-medium">Google Maps Configuration</h3>
      
//       <div className="mb-4">
//         <label className="block text-sm font-medium text-gray-700 mb-1">
//           Map Address
//         </label>
//         <input
//           type="text"
//           className="w-full p-2 border rounded"
//           value={content?.address || ''}
//           onChange={(e) => updateContent('address', e.target.value)}
//           placeholder="102-1127 14th St W, North Vancouver, BC, V7P 1J9"
//         />
//         <p className="text-xs text-gray-500 mt-1">
//           Enter the full address to display on the map.
//         </p>
//       </div>
      
//       <div className="mb-4">
//         <label className="block text-sm font-medium text-gray-700 mb-1">
//           Map Height
//         </label>
//         <input
//           type="text"
//           className="w-full p-2 border rounded"
//           value={content?.height || '400px'}
//           onChange={(e) => updateContent('height', e.target.value)}
//           placeholder="400px"
//         />
//         <p className="text-xs text-gray-500 mt-1">
//           Set the height of the map (e.g., 400px, 500px, 50vh).
//         </p>
//       </div>
      
//       <div className="mb-4">
//         <label className="block text-sm font-medium text-gray-700 mb-1">
//           Placeholder Image URL
//         </label>
//         <input
//           type="text"
//           className="w-full p-2 border rounded"
//           value={content?.placeholderImage || '/images/map.jpg'}
//           onChange={(e) => updateContent('placeholderImage', e.target.value)}
//           placeholder="/images/map.jpg"
//         />
//         <p className="text-xs text-gray-500 mt-1">
//           Image to display while the map is loading.
//         </p>
//       </div>
      
//       <div className="bg-blue-50 p-4 rounded-md mb-6">
//         <h4 className="text-blue-800 font-medium mb-2">Important Notes</h4>
//         <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
//           <li>Make sure your API key is set up correctly in the backend</li>
//           <li>The map will load dynamically after the page loads</li>
//           <li>The placeholder image will show until the map is fully loaded</li>
//         </ul>
//       </div>
//     </div>
//   );
// };

// export default GoogleMapsForm;